import React, { useState, useEffect, useRef } from 'react';
import GoogleMapReact from 'google-map-react';
import styled from 'styled-components';
import { useMedia, useIntersection } from 'react-use';
import cx from 'classnames';
import Richtext from '../global/richtext/Richtext';
import LocationsMapModel from '../../models/locations-map';
import Location from '../../models/location';
import LocationMarker from './LocationMarker';
import styles from './locations-map.module.scss';
import useGeolocation from '../../hooks/useGeolocation';
import LocationSearch from './LocationSearch';
import breakpoints from '../../constants/break-points';
import { getWeekday } from '../../helpers/date';
import { isWithinBounds, groupByStateName } from '../../helpers/maps';
import Accordion from '../global/accordion/Accordion';

interface CProps {
  backgroundColor?: string;
}

const CustomStyles = styled.div`
  ${(p: CProps) =>
    p.backgroundColor && `background-color: ${p.backgroundColor};`}
`;

type Props = {
  data: LocationsMapModel;
};

const LocationsMap = ({ data }: Props) => {
  const {
    title,
    markerIcon,
    selectedMarkerIcon,
    description,
    searchLabel,
    searchIcon,
    mapDefaultZoom,
    mapDefaultCenter,
    backgroundColor,
    locationsList,
    locationsListTitle,
    noResultsMessage,
    accordionConfig,
    dynamicSection
  } = data;

  const [center, setCenter] = useState<any>();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>(
    locationsList || []
  );
  const isMobile = useMedia(`(max-width: ${breakpoints.md}px)`);
  const intersectionRef = useRef(null);
  const intersection = useIntersection(intersectionRef, {});
  const [mapZoom, setMapZoom] = useState<number | undefined>(mapDefaultZoom);
  const [renderMap, setRenderMap] = useState(false);
  const { results } = useGeolocation(renderMap);

  useEffect(() => {
    if (results && results.latitude && results.longitude) {
      setCenter({ lat: results.latitude, lng: results.longitude });
    }
  }, [results]);

  useEffect(() => {
    if (mapBounds) {
      const newLocations = locationsList?.filter((location) =>
        isWithinBounds(mapBounds, location.address)
      );

      setFilteredLocations(newLocations || []);
    }
  }, [mapBounds]);

  useEffect(() => {
    // Map needs to be rendered only when is visible, otherwise the library may fail
    // to display the location and a empty space will be displayed
    if (
      intersection?.isIntersecting ||
      (dynamicSection && window?.location?.hash === `#${dynamicSection}`)
    ) {
      setRenderMap(true);
    }
  }, [intersection]);

  const getHours = (location: Location) => {
    return Object.entries(location.hours).map(([key, value]) => {
      return value ? (
        <div
          key={`${location.club}-${key}`}
          className={cx(styles['locations-map__location-hours-entry'], {
            [styles['locations-map__location-hours-entry--selected']]:
              getWeekday() === key
          })}
        >
          <div className={styles['locations-map__location-hours-day']}>
            {key}
          </div>
          <div>{value}</div>
        </div>
      ) : null;
    });
  };

  const handleMapChange = ({
    marginBounds,
    zoom
  }: {
    marginBounds: any;
    zoom: any;
  }) => {
    setMapBounds(marginBounds);
    setMapZoom(zoom);
  };

  const handleSearch = (newCenter: any) => {
    setCenter(newCenter);
    setMapZoom(mapDefaultZoom);
  };

  return (
    <CustomStyles backgroundColor={backgroundColor} ref={intersectionRef}>
      {renderMap && (
        <div className={styles['locations-map__map-container']}>
          <GoogleMapReact
            bootstrapURLKeys={{
              key: process.env.GOOGLE_MAPS_API_KEY,
              libraries: 'places'
            }}
            defaultCenter={mapDefaultCenter}
            defaultZoom={mapDefaultZoom}
            center={center}
            zoom={mapZoom}
            onChange={handleMapChange}
          >
            {filteredLocations?.map((location: Location) => (
              <LocationMarker
                id={`${location.club}-marker`}
                key={`${location.club}-marker`}
                icon={markerIcon}
                selectedIcon={selectedMarkerIcon}
                lat={location.address.lat}
                lng={location.address.lng}
                selected={selectedId === `${location.club}-marker`}
                setSelected={setSelectedId}
                location={location}
              />
            ))}
          </GoogleMapReact>
        </div>
      )}
      <div className={styles['locations-map__description-section']}>
        {title && (
          <Richtext
            customClasses={{
              container: styles['locations-map__title'],
              header: styles['locations-map__title-text']
            }}
            data={title}
          />
        )}
        {description && (
          <Richtext
            customClasses={{
              container: styles['locations-map__description']
            }}
            data={description}
          />
        )}
        <div className={styles['locations-map__search']}>
          <LocationSearch
            placeholder={searchLabel}
            icon={searchIcon}
            setResults={handleSearch}
            defaultCenter={mapDefaultCenter}
          />
        </div>
        <div className={styles['locations-map__separator']} />
      </div>
      <div className={styles['locations-map__locations-section']}>
        {locationsListTitle && !isMobile && (
          <div className={styles['locations-map__locations-title']}>
            {locationsListTitle}
          </div>
        )}
        {locationsList &&
          locationsList.length > 0 &&
          Object.entries(groupByStateName(locationsList)).map(
            ([state, locations]: [string, Location[]]) => (
              <Accordion
                title={state}
                key={`${state}-section`}
                customClass={styles['locations-map__state']}
                config={accordionConfig}
                shouldOpen
              >
                {locations.map((location: Location) => (
                  <div
                    key={`${location.club}-details`}
                    className={styles['locations-map__location']}
                  >
                    <div className={styles['locations-map__location-club']}>
                      {location.club}
                      <div
                        className={
                          styles['locations-map__location-club-partner']
                        }
                      >
                        {location.partner}
                      </div>
                    </div>
                    <div className={styles['locations-map__location-address']}>
                      {location.getFormattedAddress()}
                    </div>
                    {(location.phone || location.email) && (
                      <div
                        className={styles['locations-map__location-contact']}
                      >
                        {location.phone && <div>{location.phone}</div>}
                        {location.email && <div>{location.email}</div>}
                      </div>
                    )}
                    <div className={styles['locations-map__location-hours']}>
                      {getHours(location)}
                    </div>
                  </div>
                ))}
              </Accordion>
            )
          )}
        {(!locationsList || !locationsList.length) && noResultsMessage && (
          <Richtext
            customClasses={{
              container: styles['locations-map__error-message']
            }}
            data={noResultsMessage}
          />
        )}
      </div>
    </CustomStyles>
  );
};

export default LocationsMap;
